import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { FieldLabel, usePuck } from '@puckeditor/core';
import styled from 'styled-components';

import { resolveImageSrc } from '../../image-src';
import { STORE_IMAGE_ROLES, resolveImageSource, storeImageRoleLabel } from '../../store-images';
import type { ImageSource, StoreImageRole } from '../../store-images';
import { useCmsEnterpriseId } from '../cms-enterprise-context';
import { useCmsStoreImages } from '../cms-store-images-context';
import { useAssetPicker } from './asset-picker-control';
import { fieldControlCss } from './field-control-style';
import { segmentedControlCss, SEGMENTED_SELECTED_COLOR } from './segmented-control-style';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ActionRow = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: 1fr;
`;

const ActionButton = styled.button`
  background: var(--dcm-cms-image-source-action-bg, var(--puck-color-white, #fff));
  border: 1px solid var(--dcm-cms-image-source-action-border, var(--puck-color-border, #d0d4d8));
  border-radius: var(--dcm-cms-image-source-action-radius, 4px);
  color: var(--dcm-cms-image-source-action-color, var(--puck-color-text, #181b1f));
  cursor: pointer;
  font-size: 14px;
  padding: 7px 8px;
  text-align: left;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  &:focus-visible {
    outline: 2px solid var(--dcm-cms-image-source-focus, var(--puck-color-azure-05, #0b99e6));
    outline-offset: 2px;
  }
`;

const TabList = styled.div`
  ${segmentedControlCss}
  --_cms-segmented-bg: var(--dcm-cms-image-source-action-bg, var(--dcm-cms-segmented-bg, var(--puck-field-color-bg, #fff)));
  --_cms-segmented-color: var(
    --dcm-cms-image-source-action-color,
    var(--dcm-cms-segmented-color, var(--puck-color-text, #121516))
  );
  --_cms-segmented-border: var(
    --dcm-cms-image-source-action-border,
    var(--dcm-cms-segmented-border-color, var(--puck-color-border, #d0d4d8))
  );
  --_cms-segmented-radius: var(
    --dcm-cms-image-source-action-radius,
    var(--dcm-cms-segmented-radius, var(--puck-radius-m, 6px))
  );
  --_cms-segmented-selected-color: var(--dcm-cms-image-source-selected, ${SEGMENTED_SELECTED_COLOR});
  --_cms-segmented-focus: var(
    --dcm-cms-image-source-focus,
    var(--dcm-cms-segmented-focus, var(--puck-color-focus-ring, #0b99e6))
  );
`;

const TabButton = styled.button``;

const SourceChoice = styled(ActionButton)`
  width: 100%;
`;

const Choice = styled(ActionButton)<{ $selected: boolean }>`
  align-items: center;
  display: grid;
  gap: 10px;
  grid-template-columns: 48px minmax(0, 1fr) auto;
  min-height: 58px;
  width: 100%;
  ${({ $selected }) =>
    $selected
      ? 'box-shadow: inset 0 0 0 2px var(--dcm-cms-image-source-selected, var(--puck-color-azure-05, #0b99e6));'
      : ''}
`;

const Thumbnail = styled.img`
  background: var(--dcm-cms-image-source-thumbnail-bg, var(--puck-color-grey-11, #f0f2f4));
  border-radius: var(--dcm-cms-image-source-thumbnail-radius, 4px);
  height: 40px;
  object-fit: contain;
  width: 48px;
`;

const EmptyThumbnail = styled.span`
  align-items: center;
  background: var(--dcm-cms-image-source-thumbnail-bg, var(--puck-color-grey-11, #f0f2f4));
  border-radius: var(--dcm-cms-image-source-thumbnail-radius, 4px);
  color: var(--dcm-cms-image-source-muted, var(--puck-color-grey-05, #646d72));
  display: inline-flex;
  font-size: 10px;
  height: 40px;
  justify-content: center;
  text-align: center;
  width: 48px;
`;

const Summary = styled.div`
  align-items: center;
  display: grid;
  gap: 10px;
  grid-template-columns: auto minmax(0, 1fr);
`;

const SummaryLabel = styled.strong`
  color: var(--dcm-cms-image-source-action-color, var(--puck-color-text, #181b1f));
  font-size: 13px;
  line-height: 1.35;
`;

const SupportingText = styled.p<{ $error?: boolean }>`
  color: ${({ $error }) =>
    $error
      ? 'var(--dcm-cms-image-source-error, #a15c00)'
      : 'var(--dcm-cms-image-source-muted, var(--puck-color-grey-05, #646d72))'};
  font-size: 12px;
  line-height: 1.4;
  margin: 0;
`;

const UrlInput = styled.input`
  ${fieldControlCss}
  border-color: var(
    --dcm-cms-image-source-action-border,
    var(--puck-field-color-border, var(--puck-color-border, #d0d4d8))
  );
  border-radius: var(--dcm-cms-image-source-action-radius, var(--puck-field-radius, var(--puck-radius-m, 6px)));

  &:focus-visible {
    outline: 2px solid var(--dcm-cms-image-source-focus, var(--puck-color-azure-05, #0b99e6));
    outline-offset: 2px;
  }
`;

type ChooserView = 'chooser' | 'closed';
type SourceTab = 'library' | 'store' | 'url';

const SOURCE_TABS: ReadonlyArray<{ id: SourceTab; label: string }> = [
  { id: 'library', label: 'Browse Library' },
  { id: 'store', label: 'Store Image' },
  { id: 'url', label: 'Image URL' },
];

function sourceTabFor(source: ImageSource | undefined, allowStoreImages: boolean): SourceTab {
  if (allowStoreImages && source?.kind === 'store-image') return 'store';
  if (source?.kind === 'url') return 'url';
  return 'library';
}

export type ImageSourceFieldProps = {
  field: { label?: string };
  name: string;
  value: unknown;
  onChange: (value: ImageSource) => void;
  readOnly?: boolean;
  allowStoreImages?: boolean;
};

const ignoreSelectedAssetUrl = (): void => undefined;

function isMeaningfulSource(source: ImageSource | undefined): boolean {
  if (!source) return false;
  if (source.kind === 'store-image') return true;
  return source.src.trim() !== '';
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function nestedValue(record: Record<string, unknown>, path: string[]): unknown {
  return path.reduce<unknown>((current, key) => asRecord(current)[key], record);
}

function replaceNestedValue(
  record: Record<string, unknown>,
  [key, ...remainingPath]: string[],
  value: unknown
): Record<string, unknown> {
  if (!key) return record;
  return {
    ...record,
    [key]: remainingPath.length > 0 ? replaceNestedValue(asRecord(record[key]), remainingPath, value) : value,
  };
}

export function ImageSourceField({
  field,
  name,
  value,
  onChange,
  readOnly,
  allowStoreImages = true,
}: ImageSourceFieldProps): JSX.Element {
  const enterpriseId = useCmsEnterpriseId();
  const storeImages = useCmsStoreImages();
  const { selectedItem, dispatch, getSelectorForId } = usePuck();
  // Puck does not feed a custom field's `value` prop back after a direct replace. The selected store
  // item is authoritative so a blur commit is immediately visible and cannot be overwritten by a
  // stale draft when the author next opens this chooser.
  const storedValue = selectedItem ? nestedValue(asRecord(selectedItem.props), name.split('.')) : value;
  const resolved = resolveImageSource(storedValue, storeImages);
  const source = !allowStoreImages && resolved.source?.kind === 'store-image' ? undefined : resolved.source;
  const sourceTabs = useMemo(() => (allowStoreImages ? SOURCE_TABS : SOURCE_TABS.filter(({ id }) => id !== 'store')), [
    allowStoreImages,
  ]);
  const selectedRole = source?.kind === 'store-image' ? source.role : undefined;
  const selectedUrl = source?.kind === 'url' ? source.src : undefined;
  const hasSelection = isMeaningfulSource(source);
  const [view, setView] = useState<ChooserView>('closed');
  const [activeTab, setActiveTab] = useState<SourceTab>(
    hasSelection ? sourceTabFor(source, allowStoreImages) : 'library'
  );
  const [urlDraft, setUrlDraft] = useState(selectedUrl ?? '');
  const reactId = useId();
  const idBase = `image-source-${name.replace(/[^a-zA-Z0-9_-]/g, '-')}-${reactId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const changeButtonRef = useRef<HTMLButtonElement>(null);
  const replacementActionsRef = useRef<HTMLDivElement>(null);
  const chooserRef = useRef<HTMLDivElement>(null);
  const libraryTabRef = useRef<HTMLButtonElement>(null);
  const storeTabRef = useRef<HTMLButtonElement>(null);
  const urlTabRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (view === 'closed') setUrlDraft(selectedUrl ?? '');
  }, [selectedUrl, view]);

  useEffect(() => {
    if (!hasSelection) setActiveTab('library');
  }, [hasSelection]);

  const tabRefFor = useCallback((tab: SourceTab): React.RefObject<HTMLButtonElement> => {
    if (tab === 'store') return storeTabRef;
    if (tab === 'url') return urlTabRef;
    return libraryTabRef;
  }, []);

  const closeAfterSelection = useCallback(() => {
    setView('closed');
    requestAnimationFrame(() => changeButtonRef.current?.focus());
  }, []);

  const openReplacementChooser = useCallback(() => {
    const nextTab = sourceTabFor(source, allowStoreImages);
    setActiveTab(nextTab);
    setUrlDraft(source?.kind === 'url' ? source.src : '');
    setView('chooser');
    requestAnimationFrame(() => tabRefFor(nextTab).current?.focus());
  }, [allowStoreImages, source, tabRefFor]);

  const cancelReplacement = useCallback(() => {
    setUrlDraft(source?.kind === 'url' ? source.src : '');
    setView('closed');
    requestAnimationFrame(() => changeButtonRef.current?.focus());
  }, [source]);

  const handleAsset = useCallback(
    (src: string, asset: { id: string }) => {
      if (readOnly) return;
      onChange({ kind: 'library', assetId: asset.id, src });
      setUrlDraft('');
      closeAfterSelection();
    },
    [closeAfterSelection, onChange, readOnly]
  );
  const { handleClose, handleOpen, modal } = useAssetPicker(enterpriseId, ignoreSelectedAssetUrl, handleAsset);

  useEffect(() => {
    if (readOnly) handleClose();
  }, [handleClose, readOnly]);

  const selectStoreRole = useCallback(
    (role: StoreImageRole) => {
      onChange({ kind: 'store-image', role });
      setUrlDraft('');
      closeAfterSelection();
    },
    [closeAfterSelection, onChange]
  );

  const removeImage = useCallback(() => {
    onChange({ kind: 'url', src: '' });
    setActiveTab('library');
    setUrlDraft('');
    setView('closed');
    requestAnimationFrame(() => libraryTabRef.current?.focus());
  }, [onChange]);

  const activateTab = useCallback(
    (tab: SourceTab, moveFocus = false) => {
      setActiveTab(tab);
      if (moveFocus) requestAnimationFrame(() => tabRefFor(tab).current?.focus());
    },
    [tabRefFor]
  );

  const handleTabKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, tab: SourceTab) => {
      const currentIndex = sourceTabs.findIndex(({ id }) => id === tab);
      let nextIndex: number | undefined;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % sourceTabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + sourceTabs.length) % sourceTabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = sourceTabs.length - 1;
      if (nextIndex === undefined) return;
      event.preventDefault();
      activateTab(sourceTabs[nextIndex].id, true);
    },
    [activateTab, sourceTabs]
  );

  const handleChooserEscape = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Escape' || view !== 'chooser' || !hasSelection) return;
      event.preventDefault();
      cancelReplacement();
    },
    [cancelReplacement, hasSelection, view]
  );

  const sourceSummary = (() => {
    if (!source) return '';
    if (source.kind === 'store-image') {
      return `Store image · ${storeImageRoleLabel(source.role)} · ${
        storeImages?.name ?? 'No Previewing store selected'
      }`;
    }
    if (source.kind === 'library') return 'Library image';
    return 'Image URL';
  })();

  const staticPreviewSrc = source?.kind === 'library' || source?.kind === 'url' ? source.src : undefined;
  const previewSrc = source?.kind === 'store-image' ? resolved.src : staticPreviewSrc;

  const tabIdFor = (tab: SourceTab): string => `${idBase}-${tab}-tab`;
  const panelIdFor = (tab: SourceTab): string => `${idBase}-${tab}-panel`;

  const commitUrl = (returnFocus: boolean, fromBlur = false): void => {
    if (readOnly || urlDraft.trim() === '') return;
    const sourceValue: ImageSource = { kind: 'url', src: urlDraft };
    const selector = selectedItem ? getSelectorForId(selectedItem.props.id) : undefined;

    if (fromBlur && selectedItem && selector) {
      dispatch({
        type: 'replace',
        destinationIndex: selector.index,
        destinationZone: selector.zone,
        data: {
          ...selectedItem,
          props: {
            ...replaceNestedValue(asRecord(selectedItem.props), name.split('.'), sourceValue),
            id: selectedItem.props.id,
          },
        },
      });
    } else {
      onChange(sourceValue);
    }

    setView('closed');
    if (returnFocus) requestAnimationFrame(() => changeButtonRef.current?.focus());
  };

  const sourceChooser = (
    <Stack ref={chooserRef} data-testid={`image-source-chooser-${name}`}>
      <TabList role='tablist' aria-label='Image source'>
        {sourceTabs.map(({ id, label }) => (
          <TabButton
            key={id}
            ref={tabRefFor(id)}
            id={tabIdFor(id)}
            type='button'
            role='tab'
            aria-controls={panelIdFor(id)}
            aria-label={label}
            aria-selected={activeTab === id}
            tabIndex={activeTab === id ? 0 : -1}
            disabled={readOnly}
            onClick={() => activateTab(id)}
            onKeyDown={(event) => handleTabKeyDown(event, id)}
          >
            {label}
          </TabButton>
        ))}
      </TabList>

      <div
        id={panelIdFor('library')}
        role='tabpanel'
        aria-labelledby={tabIdFor('library')}
        hidden={activeTab !== 'library'}
      >
        <SourceChoice type='button' onClick={handleOpen} disabled={Boolean(readOnly) || !enterpriseId}>
          Choose an image
        </SourceChoice>
      </div>

      {allowStoreImages ? (
        <div
          id={panelIdFor('store')}
          role='tabpanel'
          aria-labelledby={tabIdFor('store')}
          hidden={activeTab !== 'store'}
        >
          <div aria-label='Store image roles' role='group'>
            <SupportingText>
              Previewing {storeImages?.name ?? 'store unavailable'}. The selected role updates automatically when this
              store changes.
            </SupportingText>
            <Stack>
              {STORE_IMAGE_ROLES.map(({ role, label }) => {
                const src = storeImages?.images[role] ?? '';
                const unavailable = !src;
                return (
                  <Choice
                    key={role}
                    type='button'
                    onClick={() => selectStoreRole(role)}
                    disabled={Boolean(readOnly) || unavailable}
                    aria-pressed={selectedRole === role}
                    aria-label={`${label}${unavailable ? ', not configured' : ''}`}
                    $selected={selectedRole === role}
                    data-testid={`image-source-role-${role}`}
                  >
                    {src ? (
                      <Thumbnail src={resolveImageSrc(src)} alt='' />
                    ) : (
                      <EmptyThumbnail>Not configured</EmptyThumbnail>
                    )}
                    <span>{label}</span>
                    <span aria-hidden='true'>{selectedRole === role ? '✓' : ''}</span>
                  </Choice>
                );
              })}
            </Stack>
          </div>
        </div>
      ) : null}

      <div id={panelIdFor('url')} role='tabpanel' aria-labelledby={tabIdFor('url')} hidden={activeTab !== 'url'}>
        <UrlInput
          type='text'
          value={urlDraft}
          onChange={(event) => setUrlDraft(event.currentTarget.value)}
          onBlur={(event) => {
            const nextTarget = event.relatedTarget as Node | null;
            if (chooserRef.current?.contains(nextTarget) || replacementActionsRef.current?.contains(nextTarget)) return;
            commitUrl(false, true);
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' || urlDraft.trim() === '') return;
            event.preventDefault();
            commitUrl(true);
          }}
          disabled={readOnly}
          placeholder='https://...'
          aria-label='Image URL'
          data-testid={`image-source-url-${name}`}
        />
      </div>
    </Stack>
  );

  return (
    <FieldLabel label={field.label ?? name} readOnly={readOnly}>
      <Stack onKeyDown={handleChooserEscape}>
        {hasSelection ? (
          <>
            <Summary>
              {previewSrc ? <Thumbnail src={resolveImageSrc(previewSrc)} alt='' /> : null}
              <SummaryLabel data-testid={`image-source-summary-${name}`}>{sourceSummary}</SummaryLabel>
            </Summary>
            {selectedRole && !resolved.src ? (
              <SupportingText $error role='status'>
                {storeImageRoleLabel(selectedRole)} is not configured for{' '}
                {storeImages?.name ?? 'the current Previewing store'}. Configure it in Store settings or choose another
                image.
              </SupportingText>
            ) : null}
            <ActionRow ref={replacementActionsRef}>
              {view === 'closed' ? (
                <ActionButton
                  ref={changeButtonRef}
                  type='button'
                  onClick={openReplacementChooser}
                  disabled={readOnly}
                  aria-expanded='false'
                  aria-label='Change image'
                >
                  Change image
                </ActionButton>
              ) : (
                <ActionButton type='button' onClick={cancelReplacement} disabled={readOnly} aria-label='Cancel'>
                  Cancel
                </ActionButton>
              )}
              <ActionButton type='button' onClick={removeImage} disabled={readOnly} aria-label='Remove image'>
                Remove image
              </ActionButton>
            </ActionRow>
            {view === 'chooser' ? sourceChooser : null}
          </>
        ) : (
          sourceChooser
        )}
      </Stack>
      {modal}
    </FieldLabel>
  );
}

/** The constrained Tile media control supports authored Asset Library and URL sources only. */
export function AssetOrUrlImageSourceField(props: ImageSourceFieldProps): JSX.Element {
  return <ImageSourceField {...props} allowStoreImages={false} />;
}
